import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePlayerSpawnsTabs, PlayerSpawnsTabsProvider, PlayerSpawnsTabState } from '../contexts/PlayerSpawnsTabsContext';
import { elasticsearchService } from '../services/elasticsearchService';
import { PlayerSpawnSource } from '../types/playerSpawn';
import { PlayerLink } from '../components/PlayerLink';
import { ItemIcon } from '../components/ItemIcon';
import { X, Calendar, User, Search, Plus, LoaderCircle, ChevronsUpDown, PlusCircle, MinusCircle, Filter, Package } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { subDays, format, formatDistanceToNow, differenceInSeconds } from 'date-fns';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { usernameCacheService } from '../services/usernameCache';
import { useDebounce } from '../hooks/useDebounce';
import { cn } from '../utils/cn';
import { useIsMobile } from '../hooks/useIsMobile';
import { PageHeader } from '../components/ui/PageHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonLoader } from '../components/SkeletonLoader';

/** Layout-mirroring placeholder rows shown while a tab is loading. */
const LogRowsSkeleton = ({ rows = 8 }: { rows?: number }) => (
  <div aria-hidden="true">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="flex items-center gap-4 px-4 py-2.5 border-b border-glass-200/[0.06]">
        <SkeletonLoader className="h-3 w-24" />
        <SkeletonLoader className="h-6 w-6 rounded-md" />
        <SkeletonLoader className="h-3 w-28" />
        <SkeletonLoader className="h-4 w-16 rounded-full max-md:hidden" />
        <SkeletonLoader className="h-3 flex-1" />
      </div>
    ))}
  </div>
);

/** Labelled slot in the filter toolbar. */
const FilterField = ({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) => (
  <div className={className}>
    <div className="text-xs font-medium text-glass-400 mb-1.5">{label}</div>
    {children}
  </div>
);

const SpawnLogMessage = ({
  spawn,
  highlightedUsers,
  onToggleHighlight,
  onFilterPlayer,
  onFilterItem,
}: {
  spawn: PlayerSpawnSource;
  highlightedUsers: { [uuid: string]: string };
  onToggleHighlight: (uuid: string) => void;
  onFilterPlayer: (uuid: string, include: boolean) => void;
  onFilterItem: (itemId: string, include: boolean) => void;
}) => {
  const isMobile = useIsMobile();
  const { '@timestamp': timestamp, player_uuid, log_type, item_id, location, quantity } = spawn;
  const [timeAgo, setTimeAgo] = useState('');
  const [isHovered, setIsHovered] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const highlightColor = highlightedUsers[player_uuid];

  const localTimestamp = useMemo(() => {
    const utcTimestamp = timestamp.endsWith('Z') ? timestamp : `${timestamp}Z`;
    return new Date(utcTimestamp);
  }, [timestamp]);

  useEffect(() => {
    const updateRelativeTime = () => {
      const seconds = differenceInSeconds(new Date(), localTimestamp);
      if (seconds < 60) {
        setTimeAgo(seconds <= 1 ? '1 second ago' : `${seconds} seconds ago`);
      } else {
        setTimeAgo(formatDistanceToNow(localTimestamp, { addSuffix: true }));
      }
    };
    updateRelativeTime();
    const interval = setInterval(updateRelativeTime, 5000);
    return () => clearInterval(interval);
  }, [localTimestamp]);

  const methodLabel = log_type === 'creative' ? 'Creative' : 'Give/Item Spawn';

  const actionButtons = (
    <>
      <button
        onClick={(e) => { e.stopPropagation(); onToggleHighlight(player_uuid); }}
        className="glass-button-sm"
        title="Highlight player"
      >
        Highlight Player
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onFilterPlayer(player_uuid, true); }}
        className="btn-icon btn-icon-sm text-emerald-400 hover:text-emerald-300"
        title="Filter for this player"
        aria-label="Filter for this player"
      >
        <PlusCircle className="w-4 h-4" />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onFilterPlayer(player_uuid, false); }}
        className="btn-icon btn-icon-sm text-rose-400 hover:text-rose-300"
        title="Exclude this player"
        aria-label="Exclude this player"
      >
        <MinusCircle className="w-4 h-4" />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onFilterItem(item_id, true); }}
        className="btn-icon btn-icon-sm text-emerald-400 hover:text-emerald-300"
        title="Filter for this item"
        aria-label="Filter for this item"
      >
        <PlusCircle className="w-4 h-4" />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onFilterItem(item_id, false); }}
        className="btn-icon btn-icon-sm text-rose-400 hover:text-rose-300"
        title="Exclude this item"
        aria-label="Exclude this item"
      >
        <MinusCircle className="w-4 h-4" />
      </button>
    </>
  );

  if (isMobile) {
    return (
      <div
        onClick={() => setIsExpanded((prev) => !prev)}
        className="font-mono text-[13px] px-3 py-2 border-b border-glass-200/10 transition-colors cursor-pointer touch-manipulation"
        style={{ backgroundColor: highlightColor || 'transparent' }}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <PlayerLink uuid={player_uuid} showUuid={false} showAvatar={true} />
          </div>
          <span
            className={cn(
              'badge flex-shrink-0',
              log_type === 'creative' ? 'badge-info' : 'badge-success'
            )}
          >
            {methodLabel}
          </span>
        </div>
        <div className="flex items-center gap-2 mt-1">
          <ItemIcon clusterId={location} itemId={item_id} displayMode="inline" className="w-5 h-5 flex-shrink-0" />
          <span className="text-glass-300 truncate">{item_id}</span>
          <span className="text-glass-300 font-semibold flex-shrink-0">×{quantity}</span>
        </div>
        <div className="text-xs text-glass-500 mt-1 tabular-nums" title={localTimestamp.toLocaleString()}>
          {timeAgo} · {location}
        </div>
        {isExpanded && (
          <div className="flex items-center gap-1 mt-2 pt-2 border-t border-glass-200/10 flex-wrap">
            {actionButtons}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="font-mono text-[13px] px-4 py-1.5 flex items-start gap-4 border-b border-glass-200/[0.06] transition-colors relative"
      style={{ backgroundColor: highlightColor || 'transparent' }}
    >
      {isHovered && (
        <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-1 z-10">
          {actionButtons}
        </div>
      )}
      <div className="flex-shrink-0 w-28 text-glass-500 pt-0.5 tabular-nums" title={localTimestamp.toLocaleString()}>
        {timeAgo}
      </div>
      <div className="flex-shrink-0 w-40 flex flex-col pt-0.5">
        <PlayerLink uuid={player_uuid} showUuid={false} showAvatar={true} />
      </div>
      <div className="flex-shrink-0">
        <span
          className={cn(
            'badge',
            log_type === 'creative' ? 'badge-info' : 'badge-success'
          )}
        >
          {methodLabel}
        </span>
      </div>
      <div className="flex-shrink-0 w-48 flex items-center gap-2 overflow-visible">
        <ItemIcon clusterId={location} itemId={item_id} displayMode="inline" className="w-5 h-5" />
      </div>
      <div className="flex-shrink-0 w-24 text-glass-300 font-semibold tabular-nums">×{quantity}</div>
      <div className="flex-shrink-0 w-24 text-blue-400/90 font-medium">{location}</div>
    </div>
  );
};

const isUUID = (str: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str.trim());

const PlayerFilterInput = ({
  value,
  onChange,
  placeholder,
  icon: Icon,
}: {
  value: string[];
  onChange: (val: string[]) => void;
  placeholder: string;
  icon: React.ComponentType<{ className?: string }>;
}) => {
  const [inputValue, setInputValue] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const debouncedQuery = useDebounce(inputValue.trim(), 300);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (debouncedQuery.length < 2) {
      setResults([]);
      return;
    }
    if (isUUID(debouncedQuery)) {
      setResults([]);
      return;
    }
    setLoading(true);
    elasticsearchService
      .searchPlayers(debouncedQuery)
      .then((res) => setResults(res.hits?.hits || []))
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  }, [debouncedQuery]);

  useEffect(() => {
    if (results.length > 0) {
      usernameCacheService.prefetchUsernames(results.map((r) => r._source?.uuid).filter(Boolean));
    }
  }, [results]);

  const addUuid = useCallback(
    (uuid: string) => {
      if (uuid && !value.includes(uuid)) onChange([...value, uuid]);
      setInputValue('');
      setShowDropdown(false);
    },
    [value, onChange]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      const trimmed = inputValue.trim();
      if (isUUID(trimmed)) {
        addUuid(trimmed);
        return;
      }
      if (results.length > 0) {
        const uuid = results[0]._source?.uuid;
        if (uuid) addUuid(uuid);
        return;
      }
      if (trimmed.length >= 2 && !loading) {
        elasticsearchService.searchPlayers(trimmed).then((res) => {
          const hits = res.hits?.hits || [];
          if (hits.length > 0 && hits[0]._source?.uuid) addUuid(hits[0]._source.uuid);
        });
      }
      return;
    }
    if (e.key === 'Backspace' && !inputValue) onChange(value.slice(0, -1));
  };

  const removeToken = (token: string) => onChange(value.filter((t: string) => t !== token));

  const handleBlur = () => {
    setTimeout(() => setShowDropdown(false), 150);
  };

  const handleFocus = () => {
    if (results.length > 0) setShowDropdown(true);
  };

  return (
    <div className="relative flex-1" ref={ref}>
      <div className="glass-input flex items-center flex-wrap gap-1 py-1 px-2">
        <Icon className="w-4 h-4 text-glass-400 flex-shrink-0 mr-0.5" />
        {value.map((token: string) => (
          <span
            key={token}
            className="chip chip-removable font-mono"
          >
            <span className="max-w-[120px] truncate" title={token}>{token}</span>
            <button type="button" onClick={() => removeToken(token)} title="Remove" aria-label={`Remove ${token}`}>
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setShowDropdown(e.target.value.trim().length >= 2 && !isUUID(e.target.value.trim()));
          }}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onFocus={handleFocus}
          placeholder={value.length === 0 ? placeholder : ''}
          className="bg-transparent outline-none flex-grow text-sm placeholder-glass-400 min-w-[80px]"
        />
      </div>
      <AnimatePresence>
        {showDropdown && (results.length > 0 || loading) && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            className="absolute z-50 w-full mt-1 glass-card p-2 max-h-48 overflow-y-auto"
          >
            {loading ? (
              <div className="flex items-center justify-center py-4">
                <LoaderCircle className="w-5 h-5 animate-spin text-glass-400" />
              </div>
            ) : (
              results.slice(0, 10).map((hit) => (
                <button
                  key={hit._id}
                  onClick={() => {
                    const uuid = hit._source?.uuid;
                    if (uuid) addUuid(uuid);
                  }}
                  className="w-full text-left px-3 py-2 rounded-md hover:bg-glass-200/10 flex items-center gap-2"
                >
                  <PlayerLink
                    uuid={hit._source?.uuid}
                    name={hit._source?.lastKnownName ?? hit._source?.nickname}
                    showUuid={true}
                    showAvatar={true}
                  />
                </button>
              ))
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const TimeRangeSelector = ({ value, onChange }: { value: { from: Date; to: Date }; onChange: (v: { from: Date; to: Date }) => void }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const presets = [
    { label: 'Last hour', from: subDays(new Date(), 1 / 24), to: new Date() },
    { label: 'Last 24 hours', from: subDays(new Date(), 1), to: new Date() },
    { label: 'Last 7 days', from: subDays(new Date(), 7), to: new Date() },
    { label: 'Last 30 days', from: subDays(new Date(), 30), to: new Date() },
  ];

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="glass-input w-full flex items-center justify-between gap-2 text-left"
      >
        <span className="truncate text-xs tabular-nums">
          {format(value.from, 'MMM d, HH:mm')} - {format(value.to, 'MMM d, HH:mm')}
        </span>
        <Calendar className="w-4 h-4 text-glass-400 flex-shrink-0" />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute z-50 w-[300px] mt-1 glass-card p-3 space-y-3 right-0"
          >
            <div className="grid grid-cols-2 gap-2">
              {presets.map((p) => (
                <button
                  key={p.label}
                  onClick={() => {
                    onChange(p);
                    setIsOpen(false);
                  }}
                  className="glass-button-sm text-center"
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div>
              <label className="block text-xs font-medium text-glass-400 mb-1.5">From</label>
              <input
                type="datetime-local"
                value={format(value.from, "yyyy-MM-dd'T'HH:mm")}
                onChange={(e) => {
                  const d = new Date(e.target.value);
                  if (!isNaN(d.getTime())) onChange({ ...value, from: d });
                }}
                className="glass-input w-full"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-glass-400 mb-1.5">To</label>
              <input
                type="datetime-local"
                value={format(value.to, "yyyy-MM-dd'T'HH:mm")}
                onChange={(e) => {
                  const d = new Date(e.target.value);
                  if (!isNaN(d.getTime())) onChange({ ...value, to: d });
                }}
                className="glass-input w-full"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const MultiSelect = ({
  options,
  selected,
  onChange,
  placeholder,
}: {
  options: string[];
  selected: string[];
  onChange: (val: string[]) => void;
  placeholder: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const toggleOption = (option: string) =>
    onChange(selected.includes(option) ? selected.filter((s) => s !== option) : [...selected, option]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="glass-input w-full flex items-center justify-between gap-2 text-left"
      >
        <span className="truncate">{selected.length > 0 ? `${selected.length} selected` : placeholder}</span>
        <ChevronsUpDown className="w-4 h-4 text-glass-400 flex-shrink-0" />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute z-50 w-full mt-1 glass-card max-h-60 overflow-y-auto"
          >
            {options.map((option) => (
              <div
                key={option}
                onClick={() => toggleOption(option)}
                className="px-3 py-2 text-sm cursor-pointer hover:bg-glass-200/10 flex items-center"
              >
                <div
                  className={cn(
                    'w-4 h-4 border rounded mr-2 flex-shrink-0',
                    selected.includes(option) ? 'bg-accent-500 border-accent-300' : 'border-glass-400'
                  )}
                />
                <span className="truncate capitalize">{option}</span>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Combobox = ({
  options,
  selected,
  onChange,
  placeholder,
}: {
  options: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const removeOption = (option: string) => onChange(selected.filter((s) => s !== option));
  const selectOption = (option: string) => {
    if (!selected.includes(option)) onChange([...selected, option]);
    setInputValue('');
    setIsOpen(false);
  };

  const filteredOptions = options.filter(
    (o) => o.toLowerCase().includes(inputValue.toLowerCase()) && !selected.includes(o)
  );

  return (
    <div ref={ref}>
      <div className="glass-input flex items-center flex-wrap gap-1 py-1 px-2" onClick={() => setIsOpen(true)}>
        {selected.map((token) => (
          <span
            key={token}
            className="chip chip-removable"
          >
            <span>{token}</span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                removeOption(token);
              }}
              title="Remove"
              aria-label={`Remove ${token}`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setIsOpen(true);
          }}
          placeholder={selected.length === 0 ? placeholder : ''}
          className="bg-transparent outline-none flex-grow text-sm placeholder-glass-400 min-w-[80px]"
        />
      </div>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="absolute z-50 w-full mt-1 glass-card max-h-60 overflow-y-auto"
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <div
                key={option}
                onClick={() => selectOption(option)}
                className="px-3 py-2 text-sm cursor-pointer hover:bg-glass-200/10 flex items-center"
              >
                {option}
              </div>
            ))
          ) : (
            <div className="px-3 py-2 text-sm text-glass-400">No results found.</div>
          )}
        </motion.div>
      )}
    </div>
  );
};

const FilterBar = ({ tab }: { tab: PlayerSpawnsTabState }) => {
  const isMobile = useIsMobile();
  const { updateFilters } = usePlayerSpawnsTabs();
  const { id, filters, aggregations } = tab;
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const hasExclude = filters.itemIdExclude.length > 0 || filters.playerUuidExclude.length > 0;
  const hasItemInclude = filters.itemIdInclude.length > 0;

  const activeFilterCount =
    filters.playerUuidSearch.length +
    filters.locationSearch.length +
    filters.logTypeSearch.length +
    filters.itemIdInclude.length +
    filters.itemIdExclude.length +
    filters.playerUuidExclude.length;

  const filterControls = (
    <>
      <FilterField label="Players">
        <div className="flex items-center gap-1">
          <PlayerFilterInput
            value={filters.playerUuidSearch}
            onChange={(val) => updateFilters(id, { playerUuidSearch: val })}
            placeholder="Search by name or UUID..."
            icon={User}
          />
        </div>
      </FilterField>
      <FilterField label="Clusters">
        <Combobox
          options={aggregations.locations}
          selected={filters.locationSearch}
          onChange={(val) => updateFilters(id, { locationSearch: val })}
          placeholder="Filter clusters..."
        />
      </FilterField>
      <FilterField label="Log type">
        <MultiSelect
          options={aggregations.logTypes.length > 0 ? aggregations.logTypes : ['creative', 'item_spawn']}
          selected={filters.logTypeSearch}
          onChange={(val) => updateFilters(id, { logTypeSearch: val })}
          placeholder="Log type"
        />
      </FilterField>
      <FilterField label="Time range">
        <TimeRangeSelector value={filters.dateRange} onChange={(val) => updateFilters(id, { dateRange: val })} />
      </FilterField>
    </>
  );

  const filterPills = (hasExclude || hasItemInclude) && (
    <div className="flex flex-wrap gap-2 text-xs">
      {filters.itemIdInclude.length > 0 && (
        <div className="flex items-center gap-1 flex-wrap">
          <span className="text-emerald-400 font-medium">Include items:</span>
          {filters.itemIdInclude.map((item) => (
            <span
              key={item}
              className="chip chip-removable font-mono bg-emerald-500/15 text-emerald-300 border-emerald-500/25"
            >
              {item}
              <button type="button" onClick={() => updateFilters(id, { itemIdInclude: filters.itemIdInclude.filter((i) => i !== item) })} className="hover:text-white" title="Remove" aria-label={`Remove ${item}`}>
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      {filters.itemIdExclude.length > 0 && (
        <div className="flex items-center gap-1 flex-wrap">
          <span className="text-rose-400 font-medium">Exclude items:</span>
          {filters.itemIdExclude.map((item) => (
            <span
              key={item}
              className="chip chip-removable font-mono bg-rose-500/15 text-rose-300 border-rose-500/25"
            >
              {item}
              <button type="button" onClick={() => updateFilters(id, { itemIdExclude: filters.itemIdExclude.filter((i) => i !== item) })} className="hover:text-white" title="Remove" aria-label={`Remove ${item}`}>
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      {filters.playerUuidExclude.length > 0 && (
        <div className="flex items-center gap-1 flex-wrap">
          <span className="text-rose-400 font-medium">Exclude players:</span>
          {filters.playerUuidExclude.map((uuid) => (
            <span
              key={uuid}
              className="chip chip-removable font-mono bg-rose-500/15 text-rose-300 border-rose-500/25"
              title={uuid}
            >
              {uuid.slice(0, 8)}…
              <button type="button" onClick={() => updateFilters(id, { playerUuidExclude: filters.playerUuidExclude.filter((u) => u !== uuid) })} className="hover:text-white" title="Remove" aria-label={`Remove ${uuid}`}>
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <div className="p-3 space-y-2 border-b border-glass-200/10">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400 pointer-events-none" />
            <input
              type="text"
              value={filters.itemIdSearch}
              onChange={(e) => updateFilters(id, { itemIdSearch: e.target.value })}
              placeholder="Item ID (e.g. minecraft:diamond)"
              className="glass-input w-full pl-9 font-mono"
            />
          </div>
          <button
            type="button"
            onClick={() => setFiltersExpanded((prev) => !prev)}
            className="glass-button flex-shrink-0 touch-manipulation"
            aria-expanded={filtersExpanded}
            aria-label="Toggle filters"
          >
            <Filter className="w-4 h-4" />
            <span>Filters</span>
            {activeFilterCount > 0 && (
              <span className="badge badge-accent px-1.5">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>
        {filtersExpanded && (
          <div className="grid grid-cols-1 gap-2">
            {filterControls}
          </div>
        )}
        {filterPills}
      </div>
    );
  }

  return (
    <div className="p-3 space-y-2 border-b border-glass-200/10">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2">
        <FilterField label="Item ID">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400 pointer-events-none" />
            <input
              type="text"
              value={filters.itemIdSearch}
              onChange={(e) => updateFilters(id, { itemIdSearch: e.target.value })}
              placeholder="Item ID (e.g. minecraft:diamond)"
              className="glass-input w-full pl-9 font-mono"
            />
          </div>
        </FilterField>
        {filterControls}
      </div>
      {filterPills}
    </div>
  );
};

const PlayerSpawnsTabContent = ({ tab }: { tab: PlayerSpawnsTabState }) => {
  const { updateTabState, updateTabName, toggleUserHighlight, updateFilters } = usePlayerSpawnsTabs();
  const { id, filters, hits, totalHits, isLoading, name, highlightedUsers } = tab;
  const prevFiltersRef = useRef(filters);
  const hitsRef = useRef(hits);
  const searchReqIdRef = useRef(0);
  hitsRef.current = hits;

  const generateTabName = useCallback((currentFilters: typeof filters) => {
    const { itemIdSearch, itemIdInclude, itemIdExclude, playerUuidSearch, playerUuidExclude, locationSearch, logTypeSearch } = currentFilters;
    if (itemIdSearch) return itemIdSearch;
    if (itemIdInclude.length > 0) return `+${itemIdInclude.length} item(s)`;
    if (itemIdExclude.length > 0) return `-${itemIdExclude.length} item(s)`;
    if (playerUuidSearch.length > 0) return `${playerUuidSearch.length} player(s)`;
    if (playerUuidExclude.length > 0) return `-${playerUuidExclude.length} player(s)`;
    if (locationSearch.length > 0) return locationSearch.join(', ');
    if (logTypeSearch.length > 0) return logTypeSearch.join(', ');
    return 'All Spawns';
  }, []);

  useEffect(() => {
    if (JSON.stringify(filters) !== JSON.stringify(prevFiltersRef.current)) {
      const prevName = generateTabName(prevFiltersRef.current);
      if (name === 'New Tab' || name === 'All Spawns' || name === prevName) {
        updateTabName(id, generateTabName(filters));
      }
      prevFiltersRef.current = filters;
    }
  }, [filters, name, id, updateTabName, generateTabName]);

  const handleSearch = useCallback(
    async (isLoadMore = false) => {
      updateTabState(id, { isLoading: true });
      const reqId = ++searchReqIdRef.current;
      const mustClauses: any[] = [];

      if (filters.itemIdSearch.trim()) {
        const q = filters.itemIdSearch.trim();
        if (q.includes(':')) {
          mustClauses.push({ term: { item_id: q } });
        } else {
          mustClauses.push({ wildcard: { item_id: `*${q}*` } });
        }
      }
      if (filters.itemIdInclude.length > 0) {
        mustClauses.push({ terms: { item_id: filters.itemIdInclude } });
      }
      if (filters.playerUuidSearch.length > 0) {
        mustClauses.push({ terms: { player_uuid: filters.playerUuidSearch } });
      }
      if (filters.locationSearch.length > 0) {
        mustClauses.push({ terms: { location: filters.locationSearch } });
      }
      if (filters.logTypeSearch.length > 0) {
        mustClauses.push({ terms: { log_type: filters.logTypeSearch } });
      }

      const mustNotClauses: any[] = [];
      if (filters.itemIdExclude.length > 0) {
        mustNotClauses.push({ terms: { item_id: filters.itemIdExclude } });
      }
      if (filters.playerUuidExclude.length > 0) {
        mustNotClauses.push({ terms: { player_uuid: filters.playerUuidExclude } });
      }

      const range: any = {};
      if (filters.dateRange.from) range.gte = filters.dateRange.from.toISOString();
      if (filters.dateRange.to) range.lte = filters.dateRange.to.toISOString();
      if (Object.keys(range).length > 0) {
        mustClauses.push({ range: { '@timestamp': range } });
      }

      const boolQuery: any = { must: mustClauses.length > 0 ? mustClauses : [{ match_all: {} }] };
      if (mustNotClauses.length > 0) boolQuery.must_not = mustNotClauses;

      const query: any = {
        size: 100,
        from: isLoadMore ? hitsRef.current.length : 0,
        sort: [{ '@timestamp': { order: 'desc' } }],
        query: { bool: boolQuery },
      };

      if (!isLoadMore) {
        query.aggs = {
          locations: { terms: { field: 'location', size: 100 } },
          log_types: { terms: { field: 'log_type', size: 10 } },
          item_ids: { terms: { field: 'item_id', size: 50 } },
        };
      }

      try {
        const data = await elasticsearchService.getPlayerSpawns(query);
        if (reqId !== searchReqIdRef.current) return;
        const newHits = isLoadMore ? [...hitsRef.current, ...data.hits.hits] : data.hits.hits;
        const totalValue = typeof data.hits.total === 'object' ? data.hits.total.value : data.hits.total;
        const newState: Partial<PlayerSpawnsTabState> = {
          hits: newHits,
          totalHits: totalValue ?? 0,
          isLoading: false,
        };
        if (!isLoadMore && (data as any).aggregations) {
          const aggs = (data as any).aggregations;
          newState.aggregations = {
            locations: aggs.locations?.buckets?.map((b: any) => b.key) || [],
            logTypes: aggs.log_types?.buckets?.map((b: any) => b.key) || [],
            itemIds: aggs.item_ids?.buckets?.map((b: any) => b.key) || [],
          };
        }
        updateTabState(id, newState);
      } catch (err) {
        if (reqId !== searchReqIdRef.current) return;
        console.error(err);
        updateTabState(id, { isLoading: false });
      }
    },
    [id, filters, updateTabState]
  );

  useEffect(() => {
    const handler = setTimeout(() => handleSearch(false), 500);
    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const loadMoreRef = useRef<HTMLDivElement>(null);
  const observer = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !isLoading && hits.length < totalHits) {
        handleSearch(true);
      }
    });
    const currentRef = loadMoreRef.current;
    if (currentRef) observer.current.observe(currentRef);
    return () => {
      if (currentRef) observer.current?.unobserve(currentRef);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hits, isLoading, totalHits]);

  useEffect(() => {
    const uuids = hits.map((h) => h._source?.player_uuid).filter(Boolean);
    if (uuids.length > 0) {
      usernameCacheService.prefetchUsernames(uuids);
    }
  }, [hits]);

  return (
    <div className="h-full flex flex-col">
      <FilterBar tab={tab} />
      <div className="flex-1 overflow-y-auto">
        {isLoading && hits.length === 0 ? (
          <LogRowsSkeleton />
        ) : !isLoading && hits.length === 0 ? (
          <EmptyState bare icon={Search} title="No spawns found" description="Try widening the time range or removing a filter." />
        ) : (
          hits.map((hit) => (
            <SpawnLogMessage
              key={hit._id}
              spawn={hit._source}
              highlightedUsers={highlightedUsers}
              onToggleHighlight={(uuid) => toggleUserHighlight(id, uuid)}
              onFilterPlayer={(uuid, include) => {
                if (include) {
                  updateFilters(id, { playerUuidSearch: [...filters.playerUuidSearch, uuid].filter((u, i, a) => a.indexOf(u) === i) });
                } else {
                  updateFilters(id, { playerUuidExclude: [...filters.playerUuidExclude, uuid].filter((u, i, a) => a.indexOf(u) === i) });
                }
              }}
              onFilterItem={(itemId, include) => {
                if (include) {
                  updateFilters(id, { itemIdInclude: [...filters.itemIdInclude, itemId].filter((u, i, a) => a.indexOf(u) === i) });
                } else {
                  updateFilters(id, { itemIdExclude: [...filters.itemIdExclude, itemId].filter((u, i, a) => a.indexOf(u) === i) });
                }
              }}
            />
          ))
        )}
        {hits.length > 0 && hits.length < totalHits && (
          <div ref={loadMoreRef} className="p-3 text-center text-xs text-glass-400">
            {isLoading ? 'Loading...' : 'Load more...'}
          </div>
        )}
      </div>
    </div>
  );
};

const PlayerSpawnsViewInternal = () => {
  const [searchParams] = useSearchParams();
  const { tabs, activeTabId, addTab, removeTab, setActiveTab, getActiveTab, updateTabName } = usePlayerSpawnsTabs();
  const activeTab = getActiveTab();
  const [editingTabId, setEditingTabId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const deepLinkAppliedRef = useRef(false);

  const handleRename = (tabId: string) => {
    if (editingName.trim()) updateTabName(tabId, editingName.trim());
    setEditingTabId(null);
  };

  // Apply deep link from URL (?player=uuid) or add default tab
  useEffect(() => {
    const playerUuid = searchParams.get('player');
    if (playerUuid && playerUuid.trim() && !deepLinkAppliedRef.current) {
      deepLinkAppliedRef.current = true;
      addTab({ playerUuidSearch: [playerUuid.trim()] }, 'Player spawns');
    } else if (tabs.length === 0 && !playerUuid) {
      addTab(undefined, 'All Spawns');
    }
  }, [searchParams, addTab, tabs.length]);

  return (
    <div className="flex flex-col h-full space-y-6">
      <PageHeader
        eyebrow="Audit Logs"
        title="Item Spawn Log"
        description="Creative and /give item spawns across every cluster. Double-click a tab to rename it."
        icon={Package}
      >
        <div className="tabs relative z-10" role="tablist" aria-label="Spawn log tabs">
          <AnimatePresence initial={false}>
            {tabs.map((tab) => {
              const isActive = activeTabId === tab.id;
              return (
                <motion.div
                  layout
                  key={tab.id}
                  role="tab"
                  aria-current={isActive ? 'true' : undefined}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn('tab cursor-pointer pr-1.5 touch-manipulation', isActive && 'text-white')}
                >
                  {isActive && (
                    <motion.div layoutId="activeTabIndicator" className="absolute inset-x-0 -bottom-0.5 h-0.5 bg-accent-400" />
                  )}
                  {editingTabId === tab.id ? (
                    <input
                      type="text"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onBlur={() => handleRename(tab.id)}
                      onKeyDown={(e) => e.key === 'Enter' && handleRename(tab.id)}
                      autoFocus
                      className="bg-transparent outline-none ring-1 ring-accent-400 rounded px-1 text-sm text-white w-36"
                    />
                  ) : (
                    <span
                      onDoubleClick={() => {
                        setEditingTabId(tab.id);
                        setEditingName(tab.name);
                      }}
                      className="relative z-10"
                      title="Double-click to rename"
                    >
                      {tab.name}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeTab(tab.id);
                    }}
                    className="relative z-10 ml-1 rounded p-1 text-glass-500 hover:text-white hover:bg-glass-200/10 touch-manipulation"
                    title="Close tab"
                    aria-label={`Close ${tab.name}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </motion.div>
              );
            })}
          </AnimatePresence>
          <button type="button" onClick={() => addTab()} className="btn-icon btn-icon-sm ml-1 shrink-0 touch-manipulation" title="New tab" aria-label="New tab">
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </PageHeader>
      <section className="flex-1 glass-card overflow-hidden">
        <div className="h-full overflow-y-auto">
          <AnimatePresence mode="wait">
            {activeTab && (
              <motion.div key={activeTab.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <PlayerSpawnsTabContent tab={activeTab} />
              </motion.div>
            )}
          </AnimatePresence>
          {!activeTab && (
            <EmptyState bare icon={Package} title="No active tabs" description="Click + to start a new search." />
          )}
        </div>
      </section>
    </div>
  );
};

export function PlayerSpawnsView() {
  useDocumentTitle('Item Spawn Log');
  return (
    <PlayerSpawnsTabsProvider>
      <PlayerSpawnsViewInternal />
    </PlayerSpawnsTabsProvider>
  );
}
